import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useBlog } from '../context/BlogContext';
import { useTheme } from '../context/ThemeContext';
import { FiCopy, FiHeart, FiEye } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import api from '../utils/api';
import { decodeId } from '../utils/urlEncoder';
import Loading from '../components/Loading';

const SinglePost = () => {
  const { darkMode } = useTheme();
  const { id: urlPath } = useParams(); // Get the URL path (title-encodedId)
  const id = decodeId(urlPath); // Decode the ID from the URL path
  const { currentPost, setCurrentPost } = useBlog();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [post, setPost] = useState(null);
  const [isLiking, setIsLiking] = useState(false);
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // First check if we have the post in context
        if (currentPost && currentPost.id === id) {
          setPost(currentPost);
          setLoading(false);
          return;
        }
        
        // Fetch from API
        const response = await api.get(`/blog/${id}`);
        
        if (response.data.success) {
          const fetchedPost = {
            id: response.data.post._id,
            title: response.data.post.title,
            descriptions: response.data.post.descriptions || [{ text: response.data.post.description || '' }],
            contentSections: response.data.post.contentSections || [{ type: 'text', content: response.data.post.content || '' }],
            image: response.data.post.image || '',
            postname: response.data.post.authorName,
            authorImage: response.data.post.authorImage || 'https://avatars.githubusercontent.com/u/123456789',
            date: response.data.post.createdAt,
            readingTime: response.data.post.readingTime,
            category: response.data.post.category,
            views: response.data.post.views,
            likes: response.data.post.likes
          };
          
          setPost(fetchedPost);
          setCurrentPost(fetchedPost);
        } else {
          setError('Blog post not found');
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
        if (error.response?.status === 404) {
          setError('Blog post not found');
        } else {
          setError('Failed to load blog post. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id, currentPost, setCurrentPost]);

  const handleLike = async () => {
    if (isLiking || !post) return;
    
    try {
      setIsLiking(true);
      const response = await api.post(`/blog/${id}/like`);
      
      if (response.data.success) {
        setPost(prev => ({
          ...prev,
          likes: response.data.likes
        }));
        toast.success('Post liked!');
      }
    } catch (error) {
      console.error('Error liking post:', error);
      toast.error('Failed to like post');
    } finally {
      setIsLiking(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{error}</h3>
          <div className="space-x-4">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Try Again
            </button>
            <Link
              to="/my-blogs"
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  // Add breadcrumb navigation at the top
  const Breadcrumb = () => (
    <nav className="w-full m-1 md:mt-0">
      <ol className="flex items-center flex-wrap text-sm text-gray-200">
        <li>
          <Link to="/my-blogs" className="hover:text-blue-400 transition-colors">
            Blog
          </Link>
        </li>
        <li className="mx-2 text-gray-400">/</li>
        <li className="font-medium text-white truncate max-w-[200px] sm:max-w-none" title={post.title}>
          {post.title}
        </li>
      </ol>
    </nav>
  );

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const renderContentSection = (section, sectionIndex) => {
    if (section.type === 'code') {
      return (
        <div 
          key={sectionIndex} 
          className="my-6 rounded-lg overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700"
        >
          <div className="flex justify-between items-center px-3 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <span className="text-xs sm:text-sm font-mono text-gray-500 dark:text-gray-400">Code</span>
            <button
              onClick={() => handleCopy(section.content)}
              className="px-2 py-1 text-xs sm:text-sm rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              title="Copy code"
              aria-label="Copy code"
            >
              <FiCopy className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Copy</span>
            </button>
          </div>
          <pre className="p-3 sm:p-4 bg-gray-900 overflow-auto" style={{ maxHeight: '32rem' }}>
            <code className="font-mono text-xs sm:text-sm text-gray-200">
              {section.content}
            </code>
          </pre>
        </div>
      );
    }
    
    // Handle markdown content
    const segments = section.content.split(/(```[\s\S]*?```|`[\s\S]*?`)/);
    
    return (
      <div key={sectionIndex} className="my-6">
        {segments.map((segment, index) => {
          // Skip empty segments
          if (!segment.trim()) return null;
          
          // Code block styling
          if (segment.startsWith('```') && segment.endsWith('```')) {
            const codeContent = segment.slice(3, -3);
            const [language, ...codeLines] = codeContent.split('\n');
            const code = language.includes('```') ? codeContent : codeLines.join('\n');
            const lang = language.includes('```') ? '' : language;

            return (
              <div 
                key={index} 
                className="my-6 rounded-lg overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex justify-between items-center px-3 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <span className="text-xs sm:text-sm font-mono text-gray-500 dark:text-gray-400">
                    {lang || 'Code'}
                  </span>
                  <button
                    onClick={() => handleCopy(code)}
                    className="px-2 py-1 text-xs sm:text-sm rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    title="Copy code"
                    aria-label="Copy code"
                  >
                    <FiCopy className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Copy</span>
                  </button>
                </div>
                <pre className="p-3 sm:p-4 bg-black overflow-auto" style={{ maxHeight: '44rem' }}>
                  <code className={`font-mono text-xs sm:text-sm text-gray-100`}>
                    {code}
                  </code>
                </pre>
              </div>
            );
          }
          
          // Inline code styling
          if (segment.startsWith('`') && segment.endsWith('`')) {
            const command = segment.slice(1, -1);
            return (
              <div 
                key={index}
                className="my-2 flex items-center justify-between bg-gray-800 dark:bg-gray-900 rounded-md py-1 px-3 shadow"
              >
                <code className="font-mono text-xs sm:text-sm text-gray-100 break-all">
                  {command}
                </code>
                <button
                  onClick={() => handleCopy(command)}
                  className="ml-2 px-2 py-1 rounded hover:bg-gray-700 transition-colors duration-200 flex items-center gap-1 text-gray-300 hover:text-white text-xs"
                  title="Copy command"
                  aria-label="Copy command"
                >
                  <FiCopy className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Copy</span>
                </button>
              </div>
            );
          }
          
          // Regular text content
          return (
            <div key={index} className="mb-4">
              <ReactMarkdown className="whitespace-pre-wrap">
                {segment}
              </ReactMarkdown>
            </div>
          );
        })}
      </div>
    );
  };



  // Function to interleave descriptions and content sections
  const renderContent = () => {
    const content = [];
    const maxLength = Math.max(
      post.descriptions?.length || 0,
      post.contentSections?.length || 0
    );

    for (let i = 0; i < maxLength; i++) {
      // Add description if it exists
      if (post.descriptions?.[i]) {
        content.push(
          <div key={`desc-${i}`} className="mb-8">
            <p className="text-lg md:text-xl capitalize leading-relaxed font-medium text-gray-600 dark:text-gray-300 border-l-4 border-blue-500 pl-1  italic">
              {post.descriptions[i].text}
            </p>
          </div>
        );
      }

      // Add content section if it exists
      if (post.contentSections?.[i]) {
        content.push(
          <div key={`content-${i}`} className="mb-8">
            {renderContentSection(post.contentSections[i], i)}
          </div>
        );
      }
    }

    return content;
  };

  return (
    <div className={`min-h-screen -mt-6 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      {/* Hero Section with Image */}
      <div className="w-full relative">
        {post.image ? (
          <img 
            src={post.image} 
            alt={post.title} 
            className="w-full h-auto max-h-[200px] object-cover"
          />
        ) : (
          <div className="w-full h-64 md:h-80 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <div className="text-center text-white px-4">
              <h2 className="text-2xl md:text-4xl font-bold mb-2">{post.category}</h2>
              <p className="text-lg md:text-xl opacity-80">Blog Post</p>
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/40" />
        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8 md:w-[85%] mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-2 md:mb-4 tracking-tight leading-tight">
            {post.title}
          </h1>
          <div className="flex relative lg:absolute bottom-0 right-0 flex-wrap items-center text-gray-200 text-xs sm:text-sm gap-2 md:gap-6 lg:mb-4">
            <span className="font-medium flex items-center">
              <img 
                src={post.authorImage} 
                alt={post.postname}
                className="w-5 h-5 md:w-6 md:h-6 rounded-full mr-1 md:mr-2"
              />
              {post.postname}
            </span>
            <span className="text-gray-300">
              {new Date(post.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </span>
            <span className="flex items-center text-gray-300">
              <FiEye className="w-3 h-3 md:w-4 md:h-4 mr-1" />
              {post.views} views
            </span>
            <button
              onClick={handleLike}
              disabled={isLiking}
              className="flex items-center text-gray-300 hover:text-red-400 transition-colors disabled:opacity-50"
              aria-label="Like post"
            >
              <FiHeart className={`w-3 h-3 md:w-4 md:h-4 mr-1 ${isLiking ? 'animate-pulse' : ''}`} />
              {post.likes} likes
            </button>
          </div>
          <Breadcrumb />
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="md:w-[85%] mx-auto  px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row lg:gap-8">
          {/* Main Content */}
          <div className="w-full lg:w-[70%] mx-auto">
            <article className={`prose ${darkMode ? 'prose-invert' : ''} prose-sm sm:prose-base md:prose-lg max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-p:leading-relaxed prose-pre:p-0 animate-fade-in`}>
              {renderContent()}
            </article>
          </div>

          {/* Right Sidebar - Only visible on large screens */}
          <div className="hidden lg:block lg:w-80 flex-shrink-0">
            <div className="sticky top-8">
              {/* Add related content or additional navigation */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SinglePost;
